import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

const CandidateForm = ({
  loading,
  closeModal,
  onSubmit,
  campaignOptions,
  initialValues,
  errors = {}, // Added errors prop to display validation messages
  notification, setNotification
}) => {
  const [formData, setFormData] = useState({
    ...initialValues,
    pan_number: initialValues.pan_number || "",
    aadhaar_number: initialValues.aadhaar_number || "",
    campaign_ids: initialValues.campaign_ids || (initialValues.campaign_id ? [initialValues.campaign_id] : []),
    application_form_id: initialValues.application_form_id || ""
  });
  const [resumeFile, setResumeFile] = useState(null); // State to manage the uploaded file
  const [formErrors, setFormErrors] = useState({}); // Validation errors

  // Sync parent API errors into form so they show on fields (normalize array messages e.g. Laravel)
  useEffect(() => {
    if (errors && typeof errors === "object" && Object.keys(errors).length > 0) {
      const normalized = {};
      Object.entries(errors).forEach(([key, val]) => {
        normalized[key] = Array.isArray(val) ? val[0] : val;
      });
      setFormErrors((prev) => ({ ...prev, ...normalized }));
    }
  }, [errors]);

  // Campaign multi-select states
  const [campaignSearchQuery, setCampaignSearchQuery] = useState("");
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const campaignDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(e.target)) {
        setShowCampaignDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filter campaigns based on search query
  const filteredCampaigns = campaignOptions.filter((campaign) =>
    campaign.label.toLowerCase().includes(campaignSearchQuery.toLowerCase())
  );

  // Handle campaign selection
  const handleCampaignSelect = (campaignId) => {
    if (formData.campaign_ids.length >= 10) {
      setNotification({
        message: "Maximum 10 campaigns can be selected.",
        type: "error"
      });
      return;
    }
    if (!formData.campaign_ids.includes(campaignId)) {
      setFormData((prev) => ({
        ...prev,
        campaign_ids: [...prev.campaign_ids, campaignId]
      }));
      // Clear error if any
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.campaign_ids;
        return newErrors;
      });
    }
    setCampaignSearchQuery("");
    setShowCampaignDropdown(false);
  };

  // Handle campaign removal
  const handleCampaignRemove = (campaignId) => {
    setFormData((prev) => ({
      ...prev,
      campaign_ids: prev.campaign_ids.filter((id) => id !== campaignId)
    }));
  };

  // Get selected campaign names
  const getSelectedCampaignNames = () => {
    return formData.campaign_ids.map((id) => {
      const campaign = campaignOptions.find((c) => c.value === id);
      return campaign ? campaign.label : "";
    }).filter(Boolean);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Handle PAN number - uppercase only, alphanumeric, max 10 chars
    if (name === "pan_number") {
      processedValue = value.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
    } else if (name === "aadhaar_number") {
      processedValue = value.replace(/\D/g, "").slice(0, 12);
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: processedValue }));
    }
    
    // Real-time validation
    const onlyTextRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    
    let error = "";
    
    if (name === "name") {
      if (!value.trim()) {
        error = "Name is required.";
      } else if (!onlyTextRegex.test(value.trim())) {
        error = "Name should contain only letters and spaces.";
      } else if (value.trim().length < 5) {
        error = "Name must be at least 5 characters.";
      } else if (value.trim().length > 50) {
        error = "Name should not exceed 50 characters.";
      }
    } else if (name === "email") {
      if (!value.trim()) {
        error = "Email is required.";
      } else if (!emailRegex.test(value.trim())) {
        error = "Invalid email format.";
      }
    } else if (name === "contact_no") {
      if (!value.trim()) {
        error = "Contact number is required.";
      } else if (value.trim().length !== 10) {
        error = "Contact number must be exactly 10 digits.";
      }
    } else if (name === "pan_number") {
      if (!processedValue.trim()) {
        error = "PAN number is required.";
      } else if (processedValue.length !== 10) {
        error = "PAN number must be exactly 10 characters.";
      } else if (!panRegex.test(processedValue)) {
        error = "Invalid PAN format. Must be 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F).";
      }
    } else if (name === "aadhaar_number") {
      const digits = (processedValue || "").replace(/\D/g, "");
      if (!digits) {
        error = "Aadhaar number is required.";
      } else if (digits.length !== 12) {
        error = "Aadhaar number must be exactly 12 digits.";
      }
    } else if (name === "experience") {
      const expValue = value === "" ? "" : parseInt(value) || "";
      if (!expValue && expValue !== 0) {
        error = "Experience is required and must be 0 or greater.";
      } else if (expValue < 0) {
        error = "Experience is required and must be 0 or greater.";
      } else if (expValue.toString().length > 2) {
        error = "Experience must be a maximum of 2 digits (0-99).";
      }
    } else if (name === "date_of_birth") {
      if (!value.trim()) {
        error = "Date of birth is required.";
      } else {
        const birthDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        
        if (age < 18) {
          error = "Person must be at least 18 years old.";
        }
      }
    }
    
    if (error) {
      setFormErrors((prev) => ({ ...prev, [name]: error }));
    } else {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e) => {
    // console.log(e.target)
    const fileInput = e.target; // Get the input element
    const file = fileInput.files[0];
  
    if (file && file.size > 2 * 1024 * 1024) { // 2MB limit
      setNotification({
        message: "File should not exceed 2MB",
        type: "error"
      });
      fileInput.value = ""; // Reset file input
      setResumeFile(null); // Clear the state
      return;
    }
    setResumeFile(file); 

  };

  const validate = () => {
    const validationErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const onlyTextRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    if (!formData.name.trim()) {
      validationErrors.name = "Name is required.";
    } else if (!onlyTextRegex.test(formData.name.trim())) {
      validationErrors.name = "Name should contain only letters and spaces.";
    } else if (formData.name.trim().length < 5) {
      validationErrors.name = "Name must be at least 5 characters.";
    } else if (formData.name.trim().length > 50) {
      validationErrors.name = "Name should not exceed 50 characters.";
    }

    if (!formData.email.trim()) {
      validationErrors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email.trim())) {
      validationErrors.email = "Invalid email format.";
    }

    if (!formData.contact_no.trim()) {
      validationErrors.contact_no = "Contact number is required.";
    } else if (formData.contact_no.trim().length !== 10) {
      validationErrors.contact_no = "Contact number must be exactly 10 digits.";
    }

    // PAN number validation
    if (!formData.pan_number || !formData.pan_number.trim()) {
      validationErrors.pan_number = "PAN number is required.";
    } else if (formData.pan_number.length !== 10) {
      validationErrors.pan_number = "PAN number must be exactly 10 characters.";
    } else if (!panRegex.test(formData.pan_number)) {
      validationErrors.pan_number = "Invalid PAN format. Must be 5 letters, 4 digits, 1 letter (e.g., ABCDE1234F).";
    }

    // Aadhaar number validation - required, exactly 12 digits
    const aadhaarDigits = (formData.aadhaar_number || "").replace(/\D/g, "");
    if (!aadhaarDigits) {
      validationErrors.aadhaar_number = "Aadhaar number is required.";
    } else if (aadhaarDigits.length !== 12) {
      validationErrors.aadhaar_number = "Aadhaar number must be exactly 12 digits.";
    }

    // Date of birth validation - required and must be at least 18 years old
    if (!formData.date_of_birth) {
      validationErrors.date_of_birth = "Date of birth is required.";
    } else {
      const birthDate = new Date(formData.date_of_birth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        validationErrors.date_of_birth = "Person must be at least 18 years old.";
      }
    }

    if (!formData.experience || formData.experience < 0) {
      validationErrors.experience = "Experience is required and must be 0 or greater.";
    } else if (formData.experience.toString().length > 2) {
      validationErrors.experience = "Experience must be a maximum of 2 digits (0-99).";
    }

    // Campaigns validation
    if (!formData.campaign_ids || formData.campaign_ids.length === 0) {
      validationErrors.campaign_ids = "At least one campaign is required.";
    } else if (formData.campaign_ids.length > 10) {
      validationErrors.campaign_ids = "Maximum 10 campaigns can be selected.";
    }

    if (!formData.resume_path && !resumeFile) {
      validationErrors.resume_path = "Resume is required.";
    }    

    return validationErrors;
  };
  
  
  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }	

    const submissionData = new FormData(); // Use FormData for file upload
    submissionData.append("name", formData.name);
    submissionData.append("email", formData.email);
    submissionData.append("contact_no", formData.contact_no);
    submissionData.append("pan_number", formData.pan_number);
    submissionData.append("aadhaar_number", (formData.aadhaar_number || "").replace(/\D/g, "").slice(0, 12));
    submissionData.append("date_of_birth", formData.date_of_birth);
    submissionData.append("experience", formData.experience);
    
    // Append campaign_id (first selected campaign) and campaign_ids array
    if (formData.campaign_ids && formData.campaign_ids.length > 0) {
      submissionData.append("campaign_id", formData.campaign_ids[0]);
      formData.campaign_ids.forEach((id) => {
        submissionData.append("campaign_ids[]", id);
      });
    }
    
    // Append application_form_id if available
    if (formData.application_form_id) {
      submissionData.append("application_form_id", formData.application_form_id);
    }

    // Append file only if it's selected
    if (resumeFile) {
      submissionData.append("resume_path", resumeFile);
    } else if (!formData.resume_path) {
      setFormErrors({ resume_path: "Resume is required." });
      return;
    }
    onSubmit(submissionData); 
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm dark:text-white font-medium text-gray-700">
            Name <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.name ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
        </div>

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm dark:text-white font-medium text-gray-700">
            Email <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.email ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.email && <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>}
        </div>

        {/* Contact Number Field */}
        <div>
          <label htmlFor="contact_no" className="block text-sm dark:text-white font-medium text-gray-700">
            Contact Number <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="tel"
            id="contact_no"
            name="contact_no"
            value={formData.contact_no}
            onChange={(e) => {
              const onlyNums = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
              setFormData({ ...formData, contact_no: onlyNums });
              
              // Real-time validation for contact number
              let error = "";
              if (!onlyNums.trim()) {
                error = "Contact number is required.";
              } else if (onlyNums.trim().length !== 10) {
                error = "Contact number must be exactly 10 digits.";
              }
              
              if (error) {
                setFormErrors((prev) => ({ ...prev, contact_no: error }));
              } else {
                setFormErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.contact_no;
                  return newErrors;
                });
              }
            }}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.contact_no ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.contact_no && (
            <p className="text-red-500 text-sm mt-1">{formErrors.contact_no}</p>
          )}
        </div>

        {/* PAN Number Field */}
        <div>
          <label htmlFor="pan_number" className="block text-sm dark:text-white font-medium text-gray-700">
            PAN Number <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="pan_number"
            name="pan_number"
            value={formData.pan_number || ""}
            onChange={handleChange}
            placeholder="ABCDE1234F"
            maxLength={10}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.pan_number ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.pan_number && (
            <p className="text-red-500 text-sm mt-1">{formErrors.pan_number}</p>
          )}
        </div>

        {/* Aadhaar Number Field */}
        <div>
          <label htmlFor="aadhaar_number" className="block text-sm dark:text-white font-medium text-gray-700">
            Aadhaar Number <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="aadhaar_number"
            name="aadhaar_number"
            value={formData.aadhaar_number || ""}
            onChange={handleChange}
            placeholder="12 digits"
            maxLength={12}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.aadhaar_number ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.aadhaar_number && (
            <p className="text-red-500 text-sm mt-1">{formErrors.aadhaar_number}</p>
          )}
        </div>

        {/* Date of Birth Field */}
        <div>
          <label htmlFor="date_of_birth" className="block text-sm dark:text-white font-medium text-gray-700">
            Date of Birth <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="date"
            id="date_of_birth"
            name="date_of_birth"
            value={formData.date_of_birth || ""}
            onChange={handleChange}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.date_of_birth ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.date_of_birth && (
            <p className="text-red-500 text-sm mt-1">{formErrors.date_of_birth}</p>
          )}
        </div>

        {/* Experience Field */}
        <div>
          <label htmlFor="experience" className="block text-sm dark:text-white font-medium text-gray-700">
            Experience (Years) <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="number"
            id="experience"
            name="experience"
            value={formData.experience || ""}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Remove non-digit characters
              const expValue = value === "" ? "" : parseInt(value) || "";
              setFormData({ ...formData, experience: expValue });
              
              // Real-time validation for experience
              let error = "";
              if (!expValue && expValue !== 0) {
                error = "Experience is required and must be 0 or greater.";
              } else if (expValue < 0) {
                error = "Experience is required and must be 0 or greater.";
              } else if (expValue.toString().length > 2) {
                error = "Experience must be a maximum of 2 digits (0-99).";
              }
              
              if (error) {
                setFormErrors((prev) => ({ ...prev, experience: error }));
              } else {
                setFormErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.experience;
                  return newErrors;
                });
              }
            }}
            min="0"
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
              formErrors.experience ? "border-red-500" : "border-gray-300"
            }`}
            required
          />
          {formErrors.experience && (
            <p className="text-red-500 text-sm mt-1">{formErrors.experience}</p>
          )}
        </div>

        {/* Campaigns Multi-Select Field */}
        <div ref={campaignDropdownRef} className="relative">
          <label htmlFor="campaigns" className="block text-sm dark:text-white font-medium text-gray-700">
            Campaigns <span className="text-red-500 ml-1">*</span>
          </label>
          
          {/* Selected Campaigns Display */}
          {formData.campaign_ids && formData.campaign_ids.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {getSelectedCampaignNames().map((campaignName, index) => {
                const campaignId = formData.campaign_ids[index];
                return (
                  <div
                    key={campaignId}
                    className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{campaignName}</span>
                    <button
                      type="button"
                      onClick={() => handleCampaignRemove(campaignId)}
                      className="ml-1 hover:text-blue-600 dark:hover:text-blue-300"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Search Input */}
          <div className="mt-1 relative">
            <input
              type="text"
              value={campaignSearchQuery}
              onChange={(e) => {
                setCampaignSearchQuery(e.target.value);
                setShowCampaignDropdown(true);
              }}
              onFocus={() => setShowCampaignDropdown(true)}
              placeholder={formData.campaign_ids.length >= 10 ? "Maximum 10 campaigns selected" : "Search and select campaigns..."}
              disabled={formData.campaign_ids.length >= 10}
              className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full border rounded-md p-2 ${
                formErrors.campaign_ids ? "border-red-500" : "border-gray-300"
              } ${formData.campaign_ids.length >= 10 ? "opacity-50 cursor-not-allowed" : ""}`}
            />
            
            {/* Dropdown */}
            {showCampaignDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                {filteredCampaigns.length > 0 ? (
                  <ul className="py-1">
                    {filteredCampaigns.map((campaign) => {
                      const isSelected = formData.campaign_ids.includes(campaign.value);
                      const isDisabled = isSelected || formData.campaign_ids.length >= 10;
                      
                      return (
                        <li
                          key={campaign.value}
                          onClick={() => !isDisabled && handleCampaignSelect(campaign.value)}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                            isDisabled ? "opacity-50 cursor-not-allowed" : ""
                          } ${isSelected ? "bg-blue-50 dark:bg-blue-900" : ""}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-900 dark:text-white">
                              {campaign.label}
                            </span>
                            {isSelected && (
                              <span className="text-xs text-blue-600 dark:text-blue-400">Selected</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
                    No campaigns found
                  </div>
                )}
              </div>
            )}
          </div>
          
          {formErrors.campaign_ids && (
            <p className="text-red-500 text-sm mt-1">{formErrors.campaign_ids}</p>
          )}
          {formData.campaign_ids.length > 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
              {formData.campaign_ids.length} of 10 campaigns selected
            </p>
          )}
        </div>



        {/* Resume Upload */}
        <div>
          <label htmlFor="resume_path" className="block text-sm dark:text-white font-medium text-gray-700">
            Resume {(!formData.resume_path && <span className="text-red-500 ml-1">*</span>)}
          </label>

          {/* Show existing resume link if available */}
          {formData.resume_path && (
            <div className="mb-2">
              <a
                href={`${"https://hrmspayroll.prepstripe.com/storage/"}${formData.resume_path}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 underline"
              >
                View Existing Resume
              </a>
            </div>
          )}

          <input
            type="file"
            id="resume_path"
            name="resume_path"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            className={`mt-1 block w-full border rounded-md p-2 ${
              formErrors.resume_path ? "border-red-500" : "border-gray-300"
            }`}
          />
          
          {formErrors.resume_path && (
            <p className="text-red-500 text-sm mt-1">{formErrors.resume_path}</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 items-center">
          <button
            type="button"
            onClick={closeModal}
            className="px-12 py-2 text-black font-medium hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex items-center gap-1 px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {initialValues.id ? "Update Candidate" : "Add Candidate"}
            {loading && (
            <svg
              className="animate-spin h-5 w-5 ml-2 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CandidateForm;